import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { BookmarkIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import MultiSelect from '../../components/stats/MultiSelect';
import StatsOverview from './StatsOverview';
import StatsPivot from './StatsPivot';
import {
  PERIMETER_LABELS, PERIOD_PRESETS, formatPeriod, fromDateInput, toDateInput,
} from '../../utils/statsFormat';

const DEFAULT_PIVOT = { rows: ['distributeur'], col: 'mois', measures: ['quantite'], sort: 'valeur', compare: false };

// Axes proposés comme filtres, dans l'ordre de la barre. Les périodes se règlent avec les dates.
const FILTER_DIMS = [
  'zone', 'region', 'distributeur', 'animateur', 'produit', 'categorie', 'type',
  'motif', 'statut', 'etape', 'source', 'canal', 'remboursement',
];

const TABS = [
  { key: 'overview', label: 'Vue d\'ensemble' },
  { key: 'pivot', label: 'Tableau croisé' },
];

const Statistics = () => {
  const [preset, setPreset] = useState('12m');
  const [range, setRange] = useState(() => PERIOD_PRESETS.find((p) => p.key === '12m').range());
  const [dateBasis, setDateBasis] = useState('created');
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [pivotSpec, setPivotSpec] = useState(DEFAULT_PIVOT);
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState('');
  const [newViewName, setNewViewName] = useState('');
  const [savingView, setSavingView] = useState(false);

  // Les valeurs proposées dans les filtres suivent la période, pas les autres filtres.
  useEffect(() => {
    let cancelled = false;
    api.statistics
      .options({ ...range, dateBasis })
      .then((res) => !cancelled && setOptions(res.data.data))
      .catch((err) => !cancelled && setError(err.response?.data?.error || 'Impossible de charger les statistiques'));
    return () => { cancelled = true; };
  }, [range, dateBasis]);

  useEffect(() => {
    let cancelled = false;
    api.statistics.views.list().then((res) => !cancelled && setViews(res.data.data)).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const query = useMemo(() => ({ ...range, dateBasis, filters }), [range, dateBasis, filters]);

  const currentConfig = () => ({
    from: range.from ? range.from.toISOString() : null,
    to: range.to ? range.to.toISOString() : null,
    dateBasis, filters, tab, pivot: pivotSpec,
  });

  const loadView = (id) => {
    setSelectedView(id);
    const view = views.find((v) => v._id === id);
    if (!view) return;
    const c = view.config || {};
    setPreset('perso');
    setRange({ from: c.from ? new Date(c.from) : null, to: c.to ? new Date(c.to) : null });
    setDateBasis(c.dateBasis || 'created');
    setFilters(c.filters || {});
    if (c.tab) setTab(c.tab);
    setPivotSpec({ ...DEFAULT_PIVOT, ...(c.pivot || {}) });
  };

  const saveView = async () => {
    const name = newViewName.trim();
    if (!name) return;
    setSavingView(true);
    try {
      const res = await api.statistics.views.create(name, currentConfig());
      setViews([res.data.data, ...views]);
      setSelectedView(res.data.data._id);
      setNewViewName('');
      toast.success('Vue enregistrée');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSavingView(false);
    }
  };

  const deleteView = async () => {
    if (!selectedView) return;
    try {
      await api.statistics.views.remove(selectedView);
      setViews(views.filter((v) => v._id !== selectedView));
      setSelectedView('');
      toast.success('Vue supprimée');
    } catch (err) {
      toast.error('Suppression impossible');
    }
  };

  const choosePreset = (key) => {
    setPreset(key);
    setRange(PERIOD_PRESETS.find((p) => p.key === key).range());
  };
  const setDate = (side, value) => {
    setPreset('perso');
    setRange({ ...range, [side]: fromDateInput(value, side === 'to') });
  };
  const setFilter = (key, values) => {
    const next = { ...filters, [key]: values };
    if (values.length === 0) delete next[key];
    setFilters(next);
  };
  const activeFilters = Object.entries(filters);
  const dimension = (key) => options?.dimensions.find((d) => d.key === key);

  if (error) {
    return <div className="p-6"><p className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</p></div>;
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Statistiques</h1>
        {options && (
          <p className="text-sm text-gray-500">
            Périmètre : <span className="font-medium text-gray-800">{PERIMETER_LABELS[options.perimeter]}</span>
            {!options.values && ' · volumes uniquement'}
          </p>
        )}
      </div>

      {/* Filtres : une seule barre, au-dessus de tout le reste */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" role="group" aria-label="Période">
            {PERIOD_PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => choosePreset(p.key)} aria-pressed={preset === p.key}
                className={`rounded px-3 py-1 text-sm ${preset === p.key ? 'bg-primary-600 font-medium text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            Du
            <input type="date" value={toDateInput(range.from)} max={toDateInput(range.to)} onChange={(e) => setDate('from', e.target.value)}
              className="rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500" />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            au
            <input type="date" value={toDateInput(range.to)} min={toDateInput(range.from)} onChange={(e) => setDate('to', e.target.value)}
              className="rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500" />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            selon la date de
            <select value={dateBasis} onChange={(e) => setDateBasis(e.target.value)}
              className="rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500">
              <option value="created">création</option>
              <option value="completed">clôture</option>
            </select>
          </label>
        </div>

        {options && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {FILTER_DIMS.map(dimension).filter((d) => d && d.values.length > 1).map((d) => (
              <MultiSelect key={d.key} label={d.label} options={d.values} value={filters[d.key] || []}
                onChange={(values) => setFilter(d.key, values)} />
            ))}
          </div>
        )}

        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            {activeFilters.flatMap(([key, values]) => values.map((value) => (
              <span key={`${key}-${value}`} className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2.5 pr-1 text-xs text-primary-900">
                <span className="text-primary-700">{dimension(key)?.label} :</span> {dimension(key)?.period ? formatPeriod(value) : value}
                <button type="button" onClick={() => setFilter(key, values.filter((v) => v !== value))}
                  className="rounded-full p-0.5 hover:bg-primary-100" aria-label={`Retirer le filtre ${value}`}>
                  <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            )))}
            <button type="button" onClick={() => setFilters({})} className="text-xs font-medium text-gray-600 underline hover:text-gray-900">
              Tout effacer
            </button>
          </div>
        )}
      </div>

      {/* Vues enregistrées (favoris), privées à l'utilisateur */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <BookmarkIcon className="h-4 w-4 text-gray-500" aria-hidden="true" /> Vues enregistrées
        </span>
        <select value={selectedView} onChange={(e) => loadView(e.target.value)} aria-label="Charger une vue enregistrée"
          className="rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500">
          <option value="">Charger une vue…</option>
          {views.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
        </select>
        {selectedView && (
          <button type="button" onClick={deleteView} title="Supprimer cette vue"
            className="inline-flex items-center rounded-md border border-gray-300 p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-700">
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Supprimer la vue</span>
          </button>
        )}
        <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
        <input type="text" value={newViewName} onChange={(e) => setNewViewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && saveView()} placeholder="Nom de la vue à enregistrer"
          aria-label="Nom de la nouvelle vue"
          className="w-52 rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500" />
        <button type="button" onClick={saveView} disabled={savingView || !newViewName.trim()}
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
          Enregistrer la vue actuelle
        </button>
      </div>

      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Vues">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} aria-current={tab === t.key ? 'page' : undefined}
              className={`border-b-2 px-1 pb-2 text-sm font-medium ${tab === t.key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {!options ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary-600" />
        </div>
      ) : tab === 'overview' ? (
        <StatsOverview query={query}
          onFilter={(key, value) => setFilter(key, [...new Set([...(filters[key] || []), value])])} />
      ) : (
        <StatsPivot query={query} options={options} spec={pivotSpec} onSpecChange={setPivotSpec} />
      )}
    </div>
  );
};

export default Statistics;
